import { Mail, ShoppingBag } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { ContactType, GetMessageQuery as GetMessage, PurchaseStatus } from '__generated__/graphql';

import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ContactFilters, {
  ContactFilterParams, mapContactFiltersToServerInput, mapServerOutputToContactFilters,
} from '../../Participants/Contacts/ContactFilters';
import EditMessageMutation from './EditMessageMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useScroll from '../../../common/useScroll';

type Message = GetMessage['project']['message'];

export interface EditMessageAudienceFormProps {
  message: Message;
}

const EditMessageAudienceForm = ({ message }: EditMessageAudienceFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const getValues = (message: Message) => ({
    filters: mapServerOutputToContactFilters(message.filters),
  });

  const form = useForm({
    values: getValues(message),
  });

  useNavigationPrompt(form.changed());

  const [editMessage, { error, loading }] = useMutation(EditMessageMutation, {
    variables: {
      input: {
        id: message.id,
        filters: mapContactFiltersToServerInput(form.values.filters),
      },
    },
    onCompleted: ({ editMessage: message }) => {
      notifier.success(t('notifications.message_saved'));

      form.reset(getValues(message));
    },
    onError: () => {
      scrollTo('MessageTab');
    },
  });

  const handleFiltersChange = (newFilters: ContactFilterParams) => {
    form.set((values) => ({
      ...values,
      filters: newFilters,
    }));
  };

  const toggleParticipants = () => form.set((values) => ({
    ...values,
    filters: {
      ...values.filters,
      contact_type: ContactType.participant,
      registration_filters: {
        event: message.event.id,
        statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
      },
      order_filters: undefined,
    },
  }));

  const toggleBuyers = () => form.set((values) => ({
    ...values,
    filters: {
      ...values.filters,
      contact_type: ContactType.buyer,
      registration_filters: undefined,
      order_filters: {
        event: message.event.id,
      },
    },
  }));

  const toggleEmailContacts = () => form.set((values) => ({
    ...values,
    filters: {
      ...values.filters,
      contact_type: ContactType.email_contact,
      registration_filters: undefined,
      order_filters: undefined,
    },
  }));

  return (
    <>
      <UI.Div sc={{ mb: 4 }}>
        <UI.SectionHeading>
          {t('audience')}
        </UI.SectionHeading>
        <UI.InputDescription>
          {t('message_audience_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card>
        <UI.Form onSubmit={() => editMessage()}>
          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.InputGroup sc={{ valid: true }}>
              <UI.InputLabel htmlFor="MessageTypeEventInformation">
                {t('send_message_to')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.FormGrid sc={{ gutter: 0.25 }}>
                <UI.Radio
                  checked={form.values.filters.contact_type === ContactType.participant}
                  onChange={toggleParticipants}
                  id="MessageTypeEventInformation"
                  sc={{ box: true }}
                >
                  <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <UI.Div>
                      <UI.Strong sc={{ block: true }}>
                        {t('participants')}
                      </UI.Strong>
                      {t('message_audience_participants_description')}
                    </UI.Div>
                    <UI.Badge sc={{ size: 42, brand: 'secondary.50', color: 'secondary' }}>
                      <UI.Icon sc={{ size: 1.5 }}>
                        <UI.Icons.Ticket />
                      </UI.Icon>
                    </UI.Badge>
                  </UI.FlexContainer>
                </UI.Radio>
                <UI.Radio
                  checked={form.values.filters.contact_type === ContactType.buyer}
                  onChange={toggleBuyers}
                  sc={{ box: true }}
                >
                  <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <UI.Div>
                      <UI.Strong sc={{ block: true }}>
                        {t('buyers')}
                      </UI.Strong>
                      {t('message_audience_buyers_description')}
                    </UI.Div>
                    <UI.Badge sc={{ size: 42, brand: 'secondary.50', color: 'secondary' }}>
                      <UI.Icon sc={{ size: 1.5 }}>
                        <ShoppingBag />
                      </UI.Icon>
                    </UI.Badge>
                  </UI.FlexContainer>
                </UI.Radio>
                <UI.Radio
                  checked={form.values.filters.contact_type === ContactType.email_contact}
                  onChange={toggleEmailContacts}
                  sc={{ box: true }}
                >
                  <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <UI.Div>
                      <UI.Strong sc={{ block: true }}>
                        {t('email_contacts')}
                      </UI.Strong>
                      {t('message_audience_email_contacts_description')}
                    </UI.Div>
                    <UI.Badge sc={{ size: 42, brand: 'secondary.50', color: 'secondary' }}>
                      <UI.Icon sc={{ size: 1.5 }}>
                        <Mail />
                      </UI.Icon>
                    </UI.Badge>
                  </UI.FlexContainer>
                </UI.Radio>
              </UI.FormGrid>
            </UI.InputGroup>

            <UI.FormGrid sc={{ gutter: 0.75 }}>
              <UI.InputLabel>
                {t('criteria')}
              </UI.InputLabel>

              <UI.HR sc={{ dotted: true }} />

              <ContactFilters
                filters={form.values.filters}
                onChange={handleFiltersChange}
                types={[
                  'registration_filters', 'order_filters', 'mail_opt_ins', 'locales',
                  'received_messages', 'inclusions', 'exclusions',
                ]}
                showResetButton={false}
              />
            </UI.FormGrid>

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_message')}
              loading={loading}
            />
          </UI.FormGrid>
        </UI.Form>
      </UI.Card>
    </>
  );
};

export default EditMessageAudienceForm;
